import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    LayoutDashboard, Users, Stethoscope, Syringe, Wallet, ReceiptText, BellRing,
    UserCog, UserRound, Trash2, FileSearch, History, LogOut, Package, PackageOpen, UserCheck, Building2, Sparkles, Settings, CalendarDays, ListChecks, Clock, ClipboardList, Radiation, FileSignature, Boxes, BookOpen, FileText, ShieldAlert }
from 'lucide-react';
import { useAuthStore } from '../store/useStore';
import AlertScadenzeMedici from './AlertScadenzeMedici';
import AlertNotifier from './AlertNotifier';
import RitardiNotifier from './RitardiNotifier';
import AttesaNotifier from './AttesaNotifier';
import { PostiAvvisi } from './postiAvvisi';
import { CodaFinestre, useStatoCoda } from './codaFinestre';
import '../styles/LayoutClassico.css';
import Assistenza from '../pages/Assistenza';
import PulsanteSegnalazione from './guscio/PulsanteSegnalazione';
import { PopupCompiti } from '../pages/Compiti';

/**
 * LayoutClassico — l'intelaiatura di prima dello stile «Console».
 *
 * E' il Layout.jsx del commit 638a8209 (il frontend in clinica fino al
 * 13/10/2026), rimesso tale e quale: cambiano solo il nome e il foglio di
 * stile, che e' il suo Layout.css di allora con ogni regola sotto
 * `.stile-classico` (styles/LayoutClassico.css). Alex, 14/10/2026: lo stile
 * nuovo non si vede in clinica finche' non e' completo al 100%, e a
 * interruttore spento (backend/services/stileNuovo.js) il gestionale e'
 * quello di prima. App.js sceglie questo guscio o quello nuovo
 * (components/Layout.jsx) dalla risposta del server, `stile_nuovo`.
 * NON SI RITOCCA per lo stile nuovo: le correzioni funzionali che lo
 * toccassero si scrivono qui E nel Layout nuovo.
 *
 * Intelaiatura comune a tutte le schermate.
 *
 * Le icone sono disegni monocromatici, non emoji: le emoji portano
 * colori propri che ignorano la palette, cambiano forma da un sistema
 * all'altro e non si possono allineare con precisione. Qui prendono
 * il colore dal testo che accompagnano.
 */

const LayoutClassico = ({ children }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const user = useAuthStore((state) => state.user);
    const logout = useAuthStore((state) => state.logout);

    /* I posti degli avvisi che arrivano da soli (components/postiAvvisi.js):
       la colonna in basso a destra e il posto nella barra. */
    const [angolo, setAngolo] = useState(null);
    const [barra, setBarra] = useState(null);
    /* Le finestre che si aprono da sole: una alla volta (codaFinestre.js).
       Finche' una e' aperta l'angolo non si offre a nessuno, cosi' il
       Promemoria e la pila non ne coprono i pulsanti. */
    const coda = useStatoCoda();

    /* La pagina lascia in fondo uno spazio alto quanto l'angolo, finche'
       ci sono avvisi: scorrendo, qualunque pulsante esce da sotto. Si
       misura l'angolo intero (Promemoria e pila insieme), e a scorrere e'
       la finestra, per questo la variabile sta sul documento. */
    useEffect(() => {
        if (!angolo || typeof ResizeObserver === 'undefined') return undefined;
        const radice = document.documentElement;
        const misura = () => {
            const h = Math.ceil(angolo.getBoundingClientRect().height);
            if (h > 0) radice.style.setProperty('--rm-spazio-pila', `${h + 108}px`);
            else radice.style.removeProperty('--rm-spazio-pila');
        };
        const o = new ResizeObserver(misura);
        o.observe(angolo);
        misura();
        return () => { o.disconnect(); radice.style.removeProperty('--rm-spazio-pila'); };
    }, [angolo]);

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const menuItems = [
        { path: '/dashboard',   Icona: LayoutDashboard, label: 'Dashboard' },
        { path: '/agenda',      Icona: CalendarDays,    label: 'Agenda' },
        { path: '/pazienti',    Icona: Users,           label: 'Pazienti' },
        { path: '/medici',      Icona: Stethoscope,     label: 'Medici' },
        { path: '/prestazioni', Icona: Syringe,         label: 'Prestazioni' },
        /* I modelli di consenso. Sta accanto alle Prestazioni e non in
           Amministrazione perche' i due elenchi sono la stessa domanda
           vista dalle due parti: «quali consensi ha questa prestazione»
           e «a cosa e' legato questo consenso». Aperta a tutti: un
           medico ci vede i propri e quelli della struttura, e il filtro
           lo fa il server. */
        { path: '/modelli-consenso', Icona: FileSignature, label: 'Consensi' },
        /* La libreria dei testi: frasi e protocolli. Sta accanto ai
           consensi perche' e' la stessa idea - un testo scritto una volta
           e usato molte - e la vedono tutti: il medico ci trova i suoi e
           quelli della struttura, l'amministrazione promuove. */
        { path: '/testi', Icona: BookOpen, label: 'Testi' },
        /* Le ricette del medico (Alex, 28/09/2026): lo stesso catalogo dei
           testi di tipo «ricetta», in una voce sua perche' il medico le cerca
           qui, non fra i testi. */
        { path: '/ricette', Icona: FileText, label: 'Ricette' },
        // Magazzino, Accettazioni, Aziende e Fatturazione sono lavoro del
        // front-desk: le rotte sono riservate ad admin e segreteria, quindi
        // qui la voce non compare ai medici, che altrimenti ci cliccherebbero
        // per essere rimandati indietro. Chi sta alla cassa le apre decine di
        // volte al giorno, percio' restano nel menu e non in Amministrazione.
        ...(user?.ruolo === 'admin' || user?.ruolo === 'segreteria'
            ? [
                { path: '/magazzino',    Icona: Package,     label: 'Magazzino' },
                /* «Voci con scadenza» (Alex, 25/09/2026): era «Pacchetti speciali», e chi
                   cercava dove si vende un pacchetto a sedute apriva questa. Il
                   percorso resta: i segnalibri non si rompono. */
                { path: '/pacchetti-speciali', Icona: PackageOpen, label: 'Voci con scadenza' },
                { path: '/accettazioni', Icona: UserCheck,   label: 'Accettazioni' },
                { path: '/soggetti',     Icona: Building2,   label: 'Aziende' },
                { path: '/fatturazione', Icona: ReceiptText, label: 'Fatturazione' },
              ]
            : []),
        /* CHI HA APERTO LE PROCEDURE RISERVATE.
           Nel menu per tutti i ruoli che la rotta ammette: il medico ci
           trova le aperture sulle PROPRIE procedure - e' lui la persona a
           cui quella traccia serve - e l'amministrazione le vede tutte.
           Una voce nascosta a un ruolo che il server autorizza e' una
           funzione che quel ruolo non sa di avere, e questa e' la
           funzione su cui si regge tutto il controllo della
           riservatezza. */
        { path: '/aperture-riservate', Icona: ShieldAlert, label: 'Aperture riservate' },
        { path: '/spettanze',   Icona: Wallet,          label: 'Spettanze' },
        { path: '/promemoria',  Icona: BellRing,        label: 'Promemoria' },
        { path: '/compiti',     Icona: ListChecks,      label: 'Compiti' },
        /* Presenze resta nel menu solo a chi NON e' admin: per un'infermiera
           e' il posto dove trova le proprie ore e le proprie buste paga, e
           deve poterci arrivare in un tocco. Per l'admin e' lavoro
           amministrativo e sta fra i riquadri di Amministrazione, insieme a
           utenti e registri - come le altre schermate che si aprono qualche
           volta al mese e non dieci volte al giorno. */
        ...(user?.ruolo !== 'admin'
            ? [{ path: '/area-dipendenti', Icona: Clock, label: 'Presenze' }]
            : []),
        /* Utenti compare anche alla segreteria: e' lei che apre l'accesso a
           una collega nuova il primo giorno. Vede l'elenco e crea utenti
           (non amministratori); password e sospensioni restano all'admin. */
        ...(user?.ruolo === 'admin' || user?.ruolo === 'segreteria'
            ? [{ path: '/utenti', Icona: UserCog, label: 'Utenti' }]
            : []),
        /* La scheda che il medico compone per se'. Sta nel menu e non
           dentro il paziente perche' si prepara una volta e poi si usa,
           come il proprio listino: cercarla dentro una cartella vorrebbe
           dire aprirne una a caso per sistemare un campo. */
        ...(user?.ruolo === 'medico' || user?.ruolo === 'medico_senior'
            ? [{ path: '/mie-schede', Icona: ClipboardList, label: 'Le mie schede' }]
            : []),
        /* Il conto prodotti. La schermata completa e' una linguetta del
           Magazzino, che ai medici e' chiuso: senza questa voce il
           permesso «ciascuno vede il proprio conto» resterebbe una rotta
           che nessuno apre. Etichetta scritta, non un'icona da scoprire
           cliccandoci sopra. */
        ...(user?.ruolo === 'medico' || user?.ruolo === 'medico_senior'
            ? [{ path: '/mio-conto-prodotti', Icona: Boxes, label: 'Il mio conto prodotti' }]
            : []),
        /* Le schede cliniche compaiono anche alla segreteria: il server le
           lascia attivare, disattivare e ordinare, e Amministrazione - dove
           sta la voce per l'admin - lei non la vede. Un permesso che esiste
           sul server e non ha una strada per arrivarci non serve a nessuno. */
        ...(user?.ruolo === 'segreteria'
            ? [{ path: '/schede-cliniche', Icona: ClipboardList, label: 'Schede cliniche' },
               { path: '/registro-rx', Icona: Radiation, label: 'Registro RX' }]
            : []),
        // Il resto sta in Amministrazione, per non allungare il menu
        // di chi lavora tutto il giorno.
        ...(user?.ruolo === 'admin'
            ? [{ path: '/amministrazione', Icona: Settings, label: 'Amministrazione' }]
            : []),
    ];

    const iniziali = `${user?.nome?.charAt(0) || ''}${user?.cognome?.charAt(0) || ''}`;

    return (
        <CodaFinestre.Provider value={coda}>
        <PostiAvvisi.Provider value={{ angolo: coda.turno ? null : angolo, barra }}>
        <div className="layout">
            <AlertScadenzeMedici user={user} />
            <AlertNotifier />
            {/* I ritardi li vede chi accoglie, non chi visita: un medico
                che sta in stanza non puo' fare niente di quello che questo
                riquadro propone - scrivere, telefonare, far accomodare - e
                un avviso che non si puo' agire e' solo un disturbo. */}
            {(user?.ruolo === 'admin' || user?.ruolo === 'segreteria') && <RitardiNotifier />}
            {/* Al medico va l'altra meta' della stessa giornata: non chi
                manca, ma chi e' gia' qui e sta aspettando lui. */}
            {(user?.ruolo === 'medico' || user?.ruolo === 'medico_senior') && <AttesaNotifier />}
            <PopupCompiti />

            <div className="header">
                <div className="logo-area">
                    {/* La versione senza payoff: nella barra il logo e'
                        alto 36 px, e a quella misura il payoff e' una
                        macchia grigia. Le dimensioni sono scritte
                        nell'attributo perche' la barra non salti mentre
                        l'immagine carica. */}
                    <img className="logo-barra"
                         src="/images/logo-rimedichub-barra.png"
                         alt="RimedicHUB"
                         width="119" height="56" />
                </div>
                <div className="header-right">
                    {/* Da tre ritardi (o attese) in su, il loro pulsante
                        sta qui: nella barra non copre niente. */}
                    <div className="rm-barra-avvisi" ref={setBarra} />
                    {/* Il nome in barra porta al proprio profilo: e' dove
                        la gente cerca le impostazioni del proprio
                        account, e finche' non ci portava era un pezzo di
                        interfaccia che sembrava cliccabile e non lo era. */}
                    <div
                        className="user-info user-info-link"
                        onClick={() => navigate('/profilo')}
                        role="button"
                        tabIndex={0}
                        title="Il mio account"
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate('/profilo'); } }}
                    >
                        <div className="user-avatar">{iniziali}</div>
                        <div>
                            <div className="user-name">{user?.nome} {user?.cognome}</div>
                            <div className="user-role">{user?.ruolo}</div>
                        </div>
                    </div>
                    {/* Il profilo non sta nel menu di sinistra: quello e'
                        organizzato per ruolo, e questa voce serve a
                        tutti e quattro i ruoli - da qui si cambia la
                        propria password e si attiva la verifica in due
                        passaggi, che per amministratori e segreteria e'
                        obbligatoria. Il nome accanto porta allo stesso
                        posto, ma senza un'etichetta nessuno lo scopre. */}
                    {/* Su schermo stretto l'etichetta sparisce e resta
                        l'icona: senza questo il pulsante non avrebbe un
                        nome per chi usa un lettore di schermo. */}
                    <button onClick={() => navigate('/profilo')} className="btn-profilo"
                            title="Il mio account" aria-label="Il mio account">
                        <UserRound size={16} strokeWidth={1.8} aria-hidden="true" />
                        <span>Il mio account</span>
                    </button>
                    <button onClick={handleLogout} className="btn-logout">
                        <LogOut size={16} strokeWidth={1.8} aria-hidden="true" />
                        <span>Esci</span>
                    </button>
                </div>
            </div>

            <div className="layout-body">
                <div className="sidebar">
                    {menuItems.map(({ path, Icona, label }) => (
                        <div
                            key={path}
                            className={`menu-item ${location.pathname === path ? 'active' : ''}`}
                            onClick={() => navigate(path)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(path); } }}
                        >
                            <span className="menu-icon">
                                <Icona size={18} strokeWidth={1.7} aria-hidden="true" />
                            </span>
                            <span className="menu-label">{label}</span>
                        </div>
                    ))}
                </div>
                <div className="main-content">
                    {children}
                </div>
            </div>
        <Assistenza />
        {/* «Segnala un problema» (migration 346): solo nel guscio, quindi mai
            sul tablet ne' al telefono dello studio; spento di serie, e spento
            non disegna niente. */}
        <PulsanteSegnalazione />
        {/* La colonna in basso a destra: Promemoria e pila, uno sopra
            l'altro, sopra il pulsante dell'assistente. */}
        <div className="rm-angolo" ref={setAngolo} />
        </div>
        </PostiAvvisi.Provider>
        </CodaFinestre.Provider>
    );
};

export default LayoutClassico;
